import { useState, useEffect, useRef } from 'react';
import './DashboardPage.css';

function ProfilePage() {
    const fileInputRef = useRef(null);
    const [user, setUser] = useState({
        name: 'Loading...',
        email: '',
        phone: '',
        kycStatus: 'Pending',
        profilePic: null,
        role: 'user',
        joinDate: ''
    });

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeModal, setActiveModal] = useState(null); // 'password', 'notifications', 'payment', 'privacy'
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        smsNotifications: true,
        loanUpdates: true,
        paymentReminders: true,
        promotionalEmails: false
    });
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [newPaymentMethod, setNewPaymentMethod] = useState({ type: 'card', cardNumber: '', cardHolder: '', expiryDate: '', cvv: '' });

    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (session && session.userId) {
            fetch(`${API_URL}/api/auth/profile/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        const userData = {
                            name: data.user.name,
                            email: data.user.email,
                            phone: data.user.phone,
                            kycStatus: data.user.kycStatus || 'In Progress',
                            role: data.user.role || 'User',
                            joinDate: new Date(data.user.joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
                            profilePic: localStorage.getItem(`profilePic_${session.userId}`) || null
                        };
                        setUser(userData);
                        setFormData(userData);
                    }
                })
                .catch(err => console.error('Error fetching profile:', err));
        }
    }, [API_URL]);

    const handleEdit = () => {
        setIsEditing(!isEditing);
        setMessage({ type: '', text: '' });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setIsSaving(true);

        const session = JSON.parse(localStorage.getItem('userSession'));

        fetch(`${API_URL}/api/auth/profile/update/${session.userId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setUser({ ...user, ...formData });
                    setIsEditing(false);
                    setMessage({ type: 'success', text: 'Profile updated successfully!' });
                } else {
                    setMessage({ type: 'error', text: 'Failed to update profile' });
                }
                setIsSaving(false);
            })
            .catch(err => {
                console.error('Error updating profile:', err);
                setMessage({ type: 'error', text: 'Failed to update profile' });
                setIsSaving(false);
            });
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleAvatarClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const session = JSON.parse(localStorage.getItem('userSession'));
                localStorage.setItem(`profilePic_${session.userId}`, reader.result);
                setUser({ ...user, profilePic: reader.result });
                setMessage({ type: 'success', text: 'Profile picture updated!' });
            };
            reader.readAsDataURL(file);
        }
    };

    // Password & Security Handlers
    const handlePasswordChange = (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match!' });
            return;
        }
        if (passwordData.newPassword.length < 8) {
            setMessage({ type: 'error', text: 'Password must be at least 8 characters!' });
            return;
        }
        setIsSaving(true);
        // Simulate API call
        setTimeout(() => {
            setMessage({ type: 'success', text: 'Password updated successfully!' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setActiveModal(null);
            setIsSaving(false);
        }, 1000);
    };

    // Notification Preferences Handlers
    const handleNotificationToggle = (key) => {
        setNotifications({ ...notifications, [key]: !notifications[key] });
    };

    const handleSaveNotifications = () => {
        setIsSaving(true);
        // Simulate API call
        setTimeout(() => {
            setMessage({ type: 'success', text: 'Notification preferences updated!' });
            setActiveModal(null);
            setIsSaving(false);
        }, 1000);
    };

    // Payment Methods Handlers
    const handleAddPaymentMethod = (e) => {
        e.preventDefault();
        const newMethod = {
            id: Date.now(),
            type: newPaymentMethod.type,
            last4: newPaymentMethod.cardNumber.slice(-4),
            cardHolder: newPaymentMethod.cardHolder,
            expiryDate: newPaymentMethod.expiryDate,
            isDefault: paymentMethods.length === 0
        };
        setPaymentMethods([...paymentMethods, newMethod]);
        setNewPaymentMethod({ type: 'card', cardNumber: '', cardHolder: '', expiryDate: '', cvv: '' });
        setMessage({ type: 'success', text: 'Payment method added successfully!' });
    };

    const handleRemovePaymentMethod = (id) => {
        setPaymentMethods(paymentMethods.filter(method => method.id !== id));
        setMessage({ type: 'success', text: 'Payment method removed!' });
    };

    const handleSetDefaultPayment = (id) => {
        setPaymentMethods(paymentMethods.map(method => ({
            ...method,
            isDefault: method.id === id
        })));
        setMessage({ type: 'success', text: 'Default payment method updated!' });
    };

    // Privacy & Data Handlers
    const handleDownloadData = () => {
        setMessage({ type: 'success', text: 'Your data download will begin shortly!' });
        // Simulate data download
        setTimeout(() => {
            const dataStr = JSON.stringify(user, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'my-profile-data.json';
            link.click();
        }, 500);
    };

    const handleDeleteAccount = () => {
        if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            setMessage({ type: 'error', text: 'Account deletion requested. Our team will contact you shortly.' });
            setActiveModal(null);
        }
    };


    return (
        <div className="dashboard-page-new">
            <div className="welcome-section">
                <div>
                    <h1>Account Settings</h1>
                    <p className="welcome-subtitle">Manage your personal information and preferences</p>
                </div>
            </div>

            {message.text && (
                <div style={{
                    padding: '1rem',
                    borderRadius: '12px',
                    backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                    color: message.type === 'success' ? '#16a34a' : '#ef4444',
                    marginBottom: '1.5rem',
                    fontWeight: '600',
                    textAlign: 'center'
                }}>
                    {message.text}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem', alignItems: 'start' }}>
                {/* Profile Card */}
                <div className="content-section" style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1.5rem' }}>
                        <div
                            onClick={handleAvatarClick}
                            style={{
                                width: '120px',
                                height: '120px',
                                borderRadius: '24px',
                                background: 'var(--primary-gradient)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '3rem',
                                color: '#000',
                                fontWeight: '800',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                border: '4px solid #fff',
                                boxShadow: '0 10px 25px rgba(247, 189, 32, 0.2)'
                            }}
                        >
                            {user.profilePic ? (
                                <img src={user.profilePic} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                user.name.charAt(0)
                            )}
                        </div>
                        <button
                            onClick={handleAvatarClick}
                            style={{
                                position: 'absolute',
                                bottom: '-5px',
                                right: '-5px',
                                width: '35px',
                                height: '35px',
                                borderRadius: '50%',
                                background: '#fff',
                                border: '1px solid #ddd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                            }}
                        >
                            📸
                        </button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            accept="image/*"
                        />
                    </div>

                    <h2 style={{ margin: '0 0 0.5rem' }}>{user.name}</h2>
                    <p style={{ color: '#64748b', margin: '0 0 1.5rem', fontWeight: '500' }}>{user.role} • Joined {user.joinDate}</p>

                    <div style={{
                        padding: '1rem',
                        background: 'var(--primary-light)',
                        borderRadius: '12px',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '10px',
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        color: '#f7bd20'
                    }}>
                        KYC Status: {user.kycStatus.toUpperCase()}
                    </div>
                </div>

                {/* Edit Form */}
                <div className="content-section">
                    <div className="section-header-new">
                        <h2>Personal Details</h2>
                        <button className="btn-secondary" onClick={handleEdit}>
                            {isEditing ? 'Cancel' : 'Edit Info'}
                        </button>
                    </div>

                    {isEditing ? (
                        <form onSubmit={handleSave}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                        required
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '2rem' }}>
                                <button type="submit" className="btn-primary" disabled={isSaving} style={{ width: '200px' }}>
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.4rem' }}>FULL NAME</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{user.name}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.4rem' }}>EMAIL ADDRESS</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{user.email}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.4rem' }}>PHONE NUMBER</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{user.phone}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.4rem' }}>MEMBER SINCE</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{user.joinDate}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Additional Settings */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Advanced Settings</h2>
                </div>
                <div className="quick-actions-grid">
                    <button className="action-card" onClick={() => setActiveModal('password')}>
                        <div className="action-icon">🔑</div>
                        <span>Password & Security</span>
                    </button>
                    <button className="action-card" onClick={() => setActiveModal('notifications')}>
                        <div className="action-icon">🔔</div>
                        <span>Notification Preferences</span>
                    </button>
                    <button className="action-card" onClick={() => setActiveModal('payment')}>
                        <div className="action-icon">💳</div>
                        <span>Payment Methods</span>
                    </button>
                    <button className="action-card" onClick={() => setActiveModal('privacy')}>
                        <div className="action-icon">🛡️</div>
                        <span>Privacy & Data</span>
                    </button>
                </div>
            </div>

            {/* Password & Security Modal */}
            {activeModal === 'password' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="content-section" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
                        <div className="section-header-new">
                            <h2>🔑 Password & Security</h2>
                            <button className="btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                        </div>
                        <form onSubmit={handlePasswordChange}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Current Password</label>
                                <input
                                    type="password"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                    required
                                />
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                    required
                                    minLength={8}
                                />
                                <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Minimum 8 characters</small>
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Confirm New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                    required
                                />
                            </div>
                            <button type="submit" className="btn-primary" disabled={isSaving} style={{ width: '100%' }}>
                                {isSaving ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Notification Preferences Modal */}
            {activeModal === 'notifications' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="content-section" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
                        <div className="section-header-new">
                            <h2>🔔 Notification Preferences</h2>
                            <button className="btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            {[
                                { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive updates via email' },
                                { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Receive updates via SMS' },
                                { key: 'loanUpdates', label: 'Loan Updates', desc: 'Get notified about loan status changes' },
                                { key: 'paymentReminders', label: 'Payment Reminders', desc: 'Reminders for upcoming payments' },
                                { key: 'promotionalEmails', label: 'Promotional Emails', desc: 'Receive offers and promotions' }
                            ].map(({ key, label, desc }) => (
                                <div key={key} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem',
                                    borderBottom: '1px solid #e5e7eb',
                                    marginBottom: '0.5rem'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{label}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{desc}</div>
                                    </div>
                                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                                        <input
                                            type="checkbox"
                                            checked={notifications[key]}
                                            onChange={() => handleNotificationToggle(key)}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span style={{
                                            position: 'absolute',
                                            cursor: 'pointer',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: notifications[key] ? '#f7bd20' : '#ccc',
                                            transition: '0.4s',
                                            borderRadius: '24px'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                content: '',
                                                height: '18px',
                                                width: '18px',
                                                left: notifications[key] ? '28px' : '3px',
                                                bottom: '3px',
                                                backgroundColor: 'white',
                                                transition: '0.4s',
                                                borderRadius: '50%'
                                            }}></span>
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                        <button onClick={handleSaveNotifications} className="btn-primary" disabled={isSaving} style={{ width: '100%' }}>
                            {isSaving ? 'Saving...' : 'Save Preferences'}
                        </button>
                    </div>
                </div>
            )}

            {/* Payment Methods Modal */}
            {activeModal === 'payment' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="content-section" style={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
                        <div className="section-header-new">
                            <h2>💳 Payment Methods</h2>
                            <button className="btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                        </div>

                        {/* Saved Payment Methods */}
                        {paymentMethods.length > 0 && (
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Saved Cards</h3>
                                {paymentMethods.map(method => (
                                    <div key={method.id} style={{
                                        padding: '1rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                                •••• •••• •••• {method.last4}
                                                {method.isDefault && <span style={{
                                                    marginLeft: '0.5rem',
                                                    padding: '0.25rem 0.5rem',
                                                    background: 'var(--primary-light)',
                                                    color: '#f7bd20',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700'
                                                }}>DEFAULT</span>}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                {method.cardHolder} • Expires {method.expiryDate}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {!method.isDefault && (
                                                <button
                                                    onClick={() => handleSetDefaultPayment(method.id)}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        borderRadius: '8px',
                                                        border: '1px solid #f7bd20',
                                                        background: 'white',
                                                        color: '#f7bd20',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Set Default
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleRemovePaymentMethod(method.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: '8px',
                                                    border: '1px solid #ef4444',
                                                    background: 'white',
                                                    color: '#ef4444',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add New Payment Method */}
                        <div>
                            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Add New Card</h3>
                            <form onSubmit={handleAddPaymentMethod}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Card Number</label>
                                    <input
                                        type="text"
                                        value={newPaymentMethod.cardNumber}
                                        onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cardNumber: e.target.value.replace(/\s/g, '').slice(0, 16) })}
                                        placeholder="1234 5678 9012 3456"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                        required
                                        pattern="[0-9]{16}"
                                    />
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Cardholder Name</label>
                                    <input
                                        type="text"
                                        value={newPaymentMethod.cardHolder}
                                        onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cardHolder: e.target.value })}
                                        placeholder="John Doe"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                        required
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Expiry Date</label>
                                        <input
                                            type="text"
                                            value={newPaymentMethod.expiryDate}
                                            onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, expiryDate: e.target.value })}
                                            placeholder="MM/YY"
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                            required
                                            pattern="(0[1-9]|1[0-2])\/[0-9]{2}"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>CVV</label>
                                        <input
                                            type="text"
                                            value={newPaymentMethod.cvv}
                                            onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cvv: e.target.value.slice(0, 3) })}
                                            placeholder="123"
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ddd' }}
                                            required
                                            pattern="[0-9]{3}"
                                        />
                                    </div>
                                </div>
                                <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                                    Add Payment Method
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy & Data Modal */}
            {activeModal === 'privacy' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="content-section" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
                        <div className="section-header-new">
                            <h2>🛡️ Privacy & Data</h2>
                            <button className="btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Data Management</h3>

                            <div style={{
                                padding: '1.5rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>📥</span>
                                    <div>
                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Download Your Data</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                            Get a copy of all your personal information
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownloadData}
                                    className="btn-secondary"
                                    style={{ marginTop: '1rem', width: '100%' }}
                                >
                                    Download Data
                                </button>
                            </div>

                            <div style={{
                                padding: '1.5rem',
                                border: '1px solid #fee2e2',
                                borderRadius: '12px',
                                background: '#fef2f2'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>⚠️</span>
                                    <div>
                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#ef4444' }}>Delete Account</div>
                                        <div style={{ fontSize: '0.85rem', color: '#991b1b' }}>
                                            Permanently delete your account and all associated data. This action cannot be undone.
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDeleteAccount}
                                    style={{
                                        marginTop: '1rem',
                                        width: '100%',
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: '#ef4444',
                                        color: 'white',
                                        fontWeight: '700',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Delete My Account
                                </button>
                            </div>
                        </div>

                        <div>
                            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Privacy Settings</h3>
                            <div style={{
                                padding: '1rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                fontSize: '0.9rem',
                                color: '#64748b'
                            }}>
                                <p style={{ marginBottom: '0.75rem' }}>
                                    <strong>Data Collection:</strong> We collect only essential information needed to provide our services.
                                </p>
                                <p style={{ marginBottom: '0.75rem' }}>
                                    <strong>Data Sharing:</strong> Your data is never sold to third parties.
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    <strong>Security:</strong> All data is encrypted and stored securely.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProfilePage;
